import { useEffect, useState } from "react";
import { UserProfileButton, UserProfileDrawer } from "@ingka-group-digital/cwds-react-user-profile";
import type { ActiveUser } from "@ingka-group-digital/cwds-react-shared-models";
import { fetchCurrentUser, type CurrentUser } from "../api/client";

function cleanName(displayName: string): string {
    // Display names can be suffixed like "Jane Doe (External - …)"; drop that.
    const idx = displayName.indexOf("(");
    return (idx > -1 ? displayName.slice(0, idx) : displayName).trim();
}

function toActiveUser(u: CurrentUser): ActiveUser {
    const name = cleanName(u.display_name);
    const parts = name.split(/\s+/).filter(Boolean);
    return {
        name,
        given_name: parts[0] || name,
        family_name: parts.length > 1 ? parts[parts.length - 1] : "",
        email: u.mail,
        jobTitle: u.job_title,
        primarySite: u.office_location,
        department: u.department,
        businessUnit: u.location,
        country: u.country,
    };
}

/**
 * Profile button + drawer (CWDS). Rendered only when authenticated; in
 * deployed environments an unauthenticated request is redirected to /auth/login
 * automatically, so there's no logged-out state to handle here.
 */
export function UserMenu() {
    const [user, setUser] = useState<CurrentUser | null>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        let alive = true;
        fetchCurrentUser().then((u) => {
            if (alive) setUser(u);
        });
        return () => {
            alive = false;
        };
    }, []);

    if (!user) return null;

    const activeUser = toActiveUser(user);

    return (
        <>
            <UserProfileButton
                user={activeUser}
                setVisibleUserProfile={setVisible}
                iconOnly
            />
            <UserProfileDrawer
                user={activeUser}
                visible={visible}
                logoutCallback={() => {
                    window.location.href = "/auth/logout";
                }}
                onUserProfileClose={() => setVisible(false)}
                userProfileLabels={{ signOut: "Log out" }}
            />
        </>
    );
}
